import { SelectionModel } from '@angular/cdk/collections';

import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ActionButton } from './models/action-button';
import { HeaderTable } from './models/header-table';

@Component({
	selector: 'app-data-table',
	templateUrl: './data-table.component.html',
	styleUrls: ['./data-table.component.scss'],
})
export class DataTableComponent implements OnInit, AfterViewInit, OnChanges {
	@ViewChild(MatPaginator, { static: true })
	paginator!: MatPaginator;
	@ViewChild('dataTable') table!: MatTable<any>;
	@ViewChild(MatSort) sort!: MatSort;
	@Input() data: any[] = [];
	@Input() headers: Array<HeaderTable> = [];
	@Input() actions: Array<ActionButton> = [];
	@Input() showFilter: boolean = true;
	@Input() moveTable: boolean = false;
	@Input() selectable: boolean = false;
	@Input() pageSizeOptions = [10, 20, 50];
	@Output() actionEmitter = new EventEmitter<any>();
	@Output() orderEmitter = new EventEmitter<any>();
	@Output() cellEmitter = new EventEmitter<any>();
	@Output() selectEmitter = new EventEmitter<any>();
	headersToDisplay: Array<HeaderTable> = [];
	dataSource: MatTableDataSource<any> = new MatTableDataSource();
	selection = new SelectionModel<any>(true, []);
	displayedColumns: string[] = [];
	showActions: boolean = false;
	showSelect: boolean = false;
	value: string = '';
	elementsSelected: any[] = [];

	ngOnInit(): void {
		this.setupTable();
	}

	ngAfterViewInit(): void {
		this.sortSetup();
	}

	ngOnChanges(): void {
		this.setupTable();
	}

	/**
	 * Emits the ordered data.
	 * @param e the type of ordering.
	 */
	sortChange(e: any): void {
		if (e.direction) {
			let orderData =
				e.direction === 'desc'
					? [...this.data].sort((d) => d[e.active])
					: [...this.data].sort((d) => d[e.active]).reverse();

			this.orderEmitter.emit(orderData);
		}
	}

	/**
	 * Setups the sorting.
	 */
	sortSetup(): void {
		this.dataSource.sort = this.sort;

		this.dataSource.sortingDataAccessor = (
			data: any,

			sortHeaderId: string
		): string => {
			if (typeof data[sortHeaderId] === 'string') {
				return data[sortHeaderId].toLocaleLowerCase();
			}

			return data[sortHeaderId];
		};
	}

	/**
	 * Emits the action clicked.
	 * @param event the event type.
	 * @param element the entity selected in the table.
	 */
	actionClicked(event: ActionButton, element: any): void {
		this.actionEmitter.emit({ eventName: event.eventName, element });
	}

	/**
	 * Moves the elements in the table.
	 * @param index The index of the element.
	 * @param moveUp To move up or down.
	 */
	moveRow(index: number, moveUp = false): void {
		let data = this.dataSource.data;

		if (moveUp) {
			let itemMove = data[index];
			let itemMoveOld = data[index - 1];
			data[index - 1] = itemMove;
			data[index] = itemMoveOld;
		} else {
			let itemMove = data[index];
			let itemMoveOld = data[index + 1];
			data[index + 1] = itemMove;
			data[index] = itemMoveOld;
		}

		this.dataSource.data = data;
		this.orderEmitter.emit(data);
		this.sortSetup();
	}

	/**
	 * Applies a filter.
	 * @param event the filter
	 */
	applyFilter(event: any): void {
		const filterValue = event.target.value;
		this.dataSource.filter = filterValue.trim().toLowerCase();
	}

	/**
	 * Emits an event when a cell is clicked
	 * @param cell the cell clicked.
	 */
	onClickCell(cell: any): void {
		this.cellEmitter.emit(cell);
	}

	selectHandler(element: any, event: MatCheckboxChange) {
		if (event.checked) {
			const index = this.elementsSelected.indexOf(element);
			if (index < 0) {
				this.elementsSelected.push(element);
			}
		} else {
			const index = this.elementsSelected.indexOf(element);
			if (index > -1) {
				this.elementsSelected.splice(index, 1);
			}
		}

		this.selectEmitter.emit(this.elementsSelected);
	}

	findProperty(element: any, propName: string): any {
		const propNames = propName.split('.');

		if (propNames.length === 1 && propNames[0] == '') {
			return element;
		}

		const firstPropName = propNames.shift();
		const restPropNames = propNames.join('.');

		if (firstPropName && !(firstPropName in element)) {
			return undefined;
		}

		return this.findProperty(element[firstPropName ?? ''], restPropNames);
	}

	/**
	 * Setups the initial table render.
	 */
	private setupTable(): void {
		this.displayedColumns = this.headers
			.filter((header) => header.display)
			.map((header) => header.property ?? '');

		this.headersToDisplay = this.headers.filter((header) => header.display);
		this.dataSource.data = this.data;

		if (this.actions.length) {
			this.displayedColumns.push('action');
			this.showActions = true;
		}

		if (this.moveTable) {
			this.displayedColumns.push('move');
		}
		if (this.selectable) {
			this.displayedColumns.unshift('select');
			this.showSelect = true;
		}

		this.dataSource.paginator = this.paginator;
	}
}
